import {
  IsEmail,
  IsPhoneNumber,
  IsNotEmpty,
  IsString,
  IsOptional,
  IsDateString,
  IsArray
} from 'class-validator';
import { IBaseResponse } from './base-response.dto';
import { Gender, type IHealthCard } from './customer.dto';
import { type PaymentMethod } from '@stripe/stripe-js';

export class IPhone {
  code: string;
  iso2: string;
  num: string;
}
export class ILeadFile {
  name: string;
  lastModified: Date;
}
export class ILead {
  _id?: string;
  uname: string;
  email: string;
  firstName: string;
  lastName: string;
  phone: string;
  gender?: Gender;
  status: LeadStatus;
  organization: string;
  createdAt?: Date;
  updatedAt?: Date;
  notes?: string;
  source: string;
  address?: {
    address_1: string;
    address_2?: string;
    city: string;
    postal: string;
    province: string;
    provinceCode?: string;
    country: string;
  };
  lastActivityAt?: Date;
  rating?: number;
  meta?: any;
  extras?: any;
  files?: ILeadFile[];
  readonly?: boolean;
  dob?: Date;
  hc?: IHealthCard;
  emrId?: string;
  dependents?: any[];
  conversationId?: string;
  isPatient?: boolean;
  paymentMethods?: PaymentMethod[];
}

export type LeadStatus =
  | 'new'
  | 'working'
  | 'lost'
  | 'unqualified'
  | 'converted'
  | 'archived';

/* -------------------------------------------------------------------------- */
/*                               Create new Lead                              */
/* -------------------------------------------------------------------------- */

export class CheckIfLeadExistByEmailDto {
  @IsNotEmpty()
  @IsEmail()
  email: string;

  @IsString()
  @IsOptional()
  organization?: string;
}
export class CheckIfLeadExistByPhoneDto {
  @IsNotEmpty()
  @IsPhoneNumber()
  phone: string;

  @IsString()
  @IsOptional()
  organization?: string;
}

export class RequestOtpByEmailDto {
  @IsNotEmpty()
  @IsEmail()
  email: string;

  @IsString()
  @IsOptional()
  organization?: string;
}
export class RequestOtpByPhoneDto {
  @IsNotEmpty()
  @IsPhoneNumber()
  phone: string;

  @IsString()
  @IsOptional()
  organization?: string;
}

export class RequestMagicLinkDto {
  @IsNotEmpty()
  @IsEmail()
  email: string;

  @IsString()
  @IsOptional()
  organization?: string;
}

export class AuthOtpDto {
  @IsNotEmpty()
  @IsString()
  code: string;

  @IsString()
  methodId: string;

  @IsString()
  @IsOptional()
  organization?: string;

  @IsString()
  @IsOptional()
  populate?: string;
}
export class AuthMagicLinkDto {
  @IsNotEmpty()
  token: string;

  @IsString()
  @IsOptional()
  organization?: string;
}

export class CreateLeadDto {
  @IsNotEmpty()
  firstName: string;

  @IsNotEmpty()
  lastName: string;

  @IsNotEmpty()
  @IsEmail()
  email: string;

  @IsNotEmpty()
  @IsPhoneNumber('CA')
  phone: string | IPhone;

  @IsString()
  @IsOptional()
  uname?: string;

  @IsString()
  notes?: string;

  gender?: string;

  source: string;

  dob?: Date;
  address?: {
    address_1?: string;
    address_2?: string;
    city?: string;
    postal?: string;
    province?: string;
    country?: string;
  };
  meta?: any;

  hc?: IHealthCard;

  emrId?: string;

  isPatient?: boolean;

  extras?: any;
}

export class CreateLeadResponse extends IBaseResponse {
  lead: ILead;
}

export class UploadFilesResponse extends IBaseResponse {
  location: string;
  lastModified: Date;
  key: string;
  name: string;
}

export class DeleteFilesResponse extends IBaseResponse {
  name: string;
  deleted?: boolean;
  declare message?: string;
}

/* -------------------------------------------------------------------------- */
/*                                Update Lead                                 */
/* -------------------------------------------------------------------------- */

export class UpdateLeadDto {
  @IsOptional()
  email?: string;
  @IsOptional()
  firstName?: string;
  @IsOptional()
  lastName?: string;
  @IsOptional()
  phone?: string;
  @IsOptional()
  notes?: string;
  @IsOptional()
  source?: string;
  @IsOptional()
  gender?: string;
  @IsOptional()
  dob?: Date;
  @IsOptional()
  hc?: IHealthCard;
  @IsOptional()
  address?: {
    address_1?: string;
    address_2?: string;
    city?: string;
    postal?: string;
    province?: string;
    country?: string;
  };
  @IsOptional()
  status?: string;
  @IsOptional()
  meta?: { [key: string]: any };
  @IsOptional()
  extras?: { [key: string]: any };
}
export class BulkUpdateLeadsDto {
  @IsArray()
  ids: string[];

  @IsOptional()
  @IsString()
  @IsNotEmpty()
  status?: string;
}

export class BulkUpsertLeadsDto {
  @IsArray()
  data: {
    email: string;
    firstName: string;
    lastName: string;
    [key: string]: string;
  }[];
}

/* -------------------------------------------------------------------------- */
/*                                  Get Leads                                 */
/* -------------------------------------------------------------------------- */
export class GetLeadsDto {
  email?: string;
  firstName?: string;
  lastName?: string;
  phone?: string;
  status?: LeadStatus;
  organization?: string;
  createdAt?: string;
  province?: string;
}

export type GetLeadsResponse = {
  results: ILead[];
  info: { page: number; size: number; total: number };
};

export class GetEmrDemographicDto {
  @IsString()
  hcNum: string;
}

export class ConvertLeadToPatientDto {
  @IsOptional()
  @IsString()
  _id?: string;

  @IsOptional()
  @IsString()
  emrId?: string;

  @IsNotEmpty()
  firstName: string;

  @IsNotEmpty()
  lastName: string;

  @IsNotEmpty()
  @IsEmail()
  email: string;

  @IsNotEmpty()
  @IsPhoneNumber('CA', { message: 'Phone format should be like +16471234567' })
  phone: string;

  @IsNotEmpty()
  @IsDateString()
  dob: string | Date;

  @IsOptional()
  @IsString()
  notes?: string;

  @IsOptional()
  @IsString()
  gender: string;

  source?: string;

  address?: {
    address_1?: string;
    address_2?: string;
    city?: string;
    postal?: string;
    province?: string;
    country?: string;
  };

  hc?: IHealthCard;
}

export type BulkUpsertLeadsResponse = {
  inserted: string[];
  upserted: string[];
  invalid: string[];
  total: number;
  row: {
    insertedIds: Record<number, string>[];
    nInserted: number;
    nMatched: number;
    nModified: number;
    nRemoved: number;
    nUpserted: number;
    ok: number;
    upserted: any[];
    writeConcernErrors: any[];
    writeErrors: any[];
  };
};
